import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { BusinessRegistrationForm, BusinessType } from '@/types/businessRegistration';
import { motion } from 'framer-motion';
import { Loader2, PawPrint, Mountain, Sparkles, Building2, ArrowRight } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { getBusinessTypesByActivityTypes } from '@/constants/businessTypes';
import { getEmojiCategoriesByActivityTypes, getDefaultEmoji } from '@/constants/emojiCategories';
import { useTranslation } from 'react-i18next';

interface BusinessStepProps {
  formData: BusinessRegistrationForm;
  setFormData: (data: BusinessRegistrationForm) => void;
  onNext: () => void;
  onBack: () => void;
  isLoading?: boolean;
  errors?: { [key: string]: string };
}

export function BusinessStep({ 
  formData, 
  setFormData, 
  onNext,
  onBack,
  isLoading = false,
  errors = {}
}: BusinessStepProps) {
  const { t } = useTranslation('business', { keyPrefix: 'registration' });
  const [localErrors, setLocalErrors] = useState<{ [key: string]: string }>({});

  const handleBusinessDataChange = (field: string, value: string | boolean) => {
    // Clear local errors when user starts typing/selecting
    setLocalErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });

    setFormData({
      ...formData,
      businessData: {
        ...formData.businessData,
        [field]: value
      }
    });
  };

  const handleEmojiSelect = (emoji: string) => {
    handleBusinessDataChange('emoji', emoji);
  };

  const handleActivityTypeChange = (type: 'hasAnimalActivities' | 'hasAdventureActivities', value: boolean) => {
    const updatedFormData = {
      ...formData,
      businessData: {
        ...formData.businessData,
        [type]: value
      }
    };

    // Auto-select appropriate business type and emoji when activity types change
    const hasAnimal = type === 'hasAnimalActivities' ? value : formData.businessData.hasAnimalActivities;
    const hasAdventure = type === 'hasAdventureActivities' ? value : formData.businessData.hasAdventureActivities;
    
    if (hasAnimal || hasAdventure) {
      const availableTypes = getBusinessTypesByActivityTypes(hasAnimal, hasAdventure);
      if (availableTypes.length > 0 && !availableTypes.find(t => t.value === updatedFormData.businessData.type)) {
        updatedFormData.businessData.type = availableTypes[0].value as BusinessType;
      }
      
      // Auto-select appropriate emoji
      const defaultEmoji = getDefaultEmoji(hasAnimal, hasAdventure);
      if (defaultEmoji) {
        updatedFormData.businessData.emoji = defaultEmoji;
      }
    }

    setFormData(updatedFormData);
  };

  const allErrors = { ...errors, ...localErrors };

  // Render helper for error messages
  const renderError = (fieldName: string) => {
    const error = allErrors[fieldName];
    return error ? (
      <p className="text-sm text-red-500 mt-1">{error}</p>
    ) : null;
  };

  // Get available business types based on selected activity types
  const availableBusinessTypes = getBusinessTypesByActivityTypes(
    formData.businessData.hasAnimalActivities,
    formData.businessData.hasAdventureActivities
  );

  // Get available emojis based on activity types
  const availableEmojiCategories = getEmojiCategoriesByActivityTypes(
    formData.businessData.hasAnimalActivities,
    formData.businessData.hasAdventureActivities
  );

  const showBusinessTypeAndIcon = formData.businessData.hasAnimalActivities || formData.businessData.hasAdventureActivities;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="space-y-6"
    >
      {/* Header Section with Clear Purpose */}
      <div className="text-center space-y-3 pb-6 border-b border-gray-200">
        <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary to-[#97BC62] rounded-full flex items-center justify-center">
          <Building2 className="h-8 w-8 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900">{t('businessInformationTitle')}</h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            {t('businessInfoDescription')}
          </p>
        </div>
      </div>

      {/* Business Basic Information */}
      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="businessName" className="flex items-center gap-1.5 text-sm font-medium">
            <Building2 className="h-4 w-4 text-primary" />
            {t('businessName')}
          </Label>
          <Input
            id="businessName"
            value={formData.businessData.name}
            onChange={(e) => handleBusinessDataChange('name', e.target.value)}
            className={`${allErrors.businessName ? 'border-red-500' : 'border-gray-300 focus:border-primary focus:ring-primary/20'}`}
            placeholder={t('businessNamePlaceholder')}
            disabled={isLoading}
            aria-invalid={!!allErrors.businessName}
          />
          {renderError('businessName')}
          <p className="text-xs text-gray-500">
            {t('businessNameHelper')}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description" className="flex items-center gap-1.5 text-sm font-medium">
            <Sparkles className="h-4 w-4 text-primary" />
            {t('businessDescription')}
          </Label>
          <Textarea
            id="description"
            value={formData.businessData.description}
            onChange={(e) => handleBusinessDataChange('description', e.target.value)}
            className={`min-h-[100px] ${allErrors.description ? 'border-red-500' : 'border-gray-300 focus:border-primary focus:ring-primary/20'}`}
            placeholder={t('businessDescriptionPlaceholder')}
            disabled={isLoading}
            aria-invalid={!!allErrors.description}
          />
          {renderError('description')}
          <p className="text-xs text-gray-500">
            {t('businessDescriptionHelper')}
          </p>
        </div>
      </div>

      {/* Activity Types Section - Enhanced with better UX */}
      <motion.div 
        className="space-y-4 bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-xl border border-green-200"
        initial={{ scale: 0.98 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h4 className="font-semibold text-gray-900">{t('activityTypesTitle')}</h4>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          {t('activityTypesDescription')}
        </p>

        <div className="space-y-4">
          <motion.div 
            className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-orange-300 transition-all duration-200 shadow-sm"
            whileHover={{ scale: 1.02, shadow: "0 4px 12px rgba(0,0,0,0.1)" }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg transition-colors ${
                formData.businessData.hasAnimalActivities 
                  ? 'bg-orange-100 text-orange-600' 
                  : 'bg-gray-100 text-gray-500'
              }`}>
                <PawPrint className="h-6 w-6" />
              </div>
              <div>
                <Label className="font-medium text-gray-900 cursor-pointer">{t('animalActivities')}</Label>
                <p className="text-sm text-gray-500">{t('animalActivitiesDescription')}</p>
              </div>
            </div>
            <Switch
              checked={formData.businessData.hasAnimalActivities}
              onCheckedChange={(checked) => handleActivityTypeChange('hasAnimalActivities', checked)}
              disabled={isLoading}
              className="data-[state=checked]:bg-orange-500"
            />
          </motion.div>

          <motion.div 
            className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-all duration-200 shadow-sm"
            whileHover={{ scale: 1.02, shadow: "0 4px 12px rgba(0,0,0,0.1)" }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg transition-colors ${
                formData.businessData.hasAdventureActivities 
                  ? 'bg-blue-100 text-blue-600' 
                  : 'bg-gray-100 text-gray-500'
              }`}>
                <Mountain className="h-6 w-6" />
              </div>
              <div>
                <Label className="font-medium text-gray-900 cursor-pointer">{t('adventureActivities')}</Label>
                <p className="text-sm text-gray-500">{t('adventureActivitiesDescription')}</p>
              </div>
            </div>
            <Switch
              checked={formData.businessData.hasAdventureActivities}
              onCheckedChange={(checked) => handleActivityTypeChange('hasAdventureActivities', checked)}
              disabled={isLoading}
              className="data-[state=checked]:bg-blue-500"
            />
          </motion.div>
        </div>

        {allErrors.activityTypes && (
          <p className="text-sm text-red-500 mt-2 bg-red-50 p-2 rounded border border-red-200">
            {allErrors.activityTypes}
          </p>
        )}
      </motion.div>

      {/* Business Type and Icon Selection - Only show when activity types are selected */}
      {showBusinessTypeAndIcon && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          {/* Business Type Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <h4 className="font-semibold text-gray-900">{t('businessCategoryTitle')}</h4>
            </div>
            <p className="text-sm text-gray-600">
              {t('businessCategoryDescription')}
              {availableBusinessTypes.length < 8 && t('businessCategoryDescriptionHelper')}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {availableBusinessTypes.map(type => (
                <motion.button
                  key={type.value}
                  type="button"
                  onClick={() => handleBusinessDataChange('type', type.value)}
                  className={`p-4 rounded-lg border text-left transition-all hover:shadow-md ${
                    formData.businessData.type === type.value
                      ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  disabled={isLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{type.emoji}</span>
                    <div>
                      <div className="font-medium text-gray-900">{type.label}</div>
                      <div className="text-xs text-gray-500 mt-1">{type.description}</div>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Business Icon Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h4 className="font-semibold text-gray-900">{t('businessIconTitle')}</h4>
            </div>
            <p className="text-sm text-gray-600">
              {t('businessIconDescription')}
            </p>
            
            <div className="space-y-3">
              {availableEmojiCategories.map(category => (
                <div key={category.name} className="space-y-2">
                  <h5 className="text-sm font-medium text-gray-700">{category.name}</h5>
                  <div className="flex flex-wrap gap-2">
                    {category.emojis.map(emoji => (
                      <motion.button
                        key={emoji}
                        type="button"
                        onClick={() => handleEmojiSelect(emoji)}
                        className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center text-xl transition-all hover:scale-110 ${
                          formData.businessData.emoji === emoji
                            ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        disabled={isLoading}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {emoji}
                      </motion.button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button 
          onClick={onBack}
          variant="outline"
          disabled={isLoading}
          className="transition-all duration-200"
        >
          {t('back')}
        </Button>
        <Button 
          onClick={onNext} 
          disabled={isLoading || (!formData.businessData.hasAnimalActivities && !formData.businessData.hasAdventureActivities)}
          className="min-w-[120px] bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 transition-all duration-200"
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('validating')}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {t('continue')}
              <ArrowRight className="w-4 h-4" />
            </span>
          )}
        </Button>
      </div>
    </motion.div>
  );
}